import React, { useContext, useCallback, FunctionComponent, useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { GetNewsDetailsById_newsitems as newsitem } from "src/api/generated/GetNewsDetailsById";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { useForm } from "react-hook-form";
import { MessageType } from "src/components/notifications/Notifier";
import { DELETE_NEWSFILE_BY_FILEID, NewsState } from "src/api/news";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { isEmpty } from "lodash";
import { useMutation } from "@apollo/client";
import { DELETE_NEWS_BY_ID } from "src/api/news";
import { DeleteNewsById, DeleteNewsByIdVariables } from "src/api/generated/DeleteNewsById";
import { DeleteNewsFileByFileId, DeleteNewsFileByFileIdVariables } from "src/api/generated/DeleteNewsFileByFileId";
import { useHistory } from "react-router-dom";
import { Route, selectRoute } from "src/config/routes";
import { getLang } from "src/utils/i18n";
import { NewsPreview } from "src/components/news/preview/NewsPreview";
import { resizeImageFile, fileToBase64 } from "src/utils/Image";
import { useSaveFormByShortcut } from "src/hooks/form/useSaveFormByShortcut";
import { FORM_STATUS } from "src/utils/Form";
import { useSaveUnsavedChangesInForm } from "src/hooks/save-unsaved-changes-in-form/useSaveUnsavedChangesInForm";
import { useNewsImageUpload } from "src/hooks/news/useNewsImageUpload";
import styles from "./NewsEditForm.module.css";
import { AddNewsDocumentsModal } from "./modal/AddNewsDocumentsModal";
import { NewsPublishfromModal } from "./modal/NewsPublishfromModal";
import { NewsImageSection } from "./section/NewsImageSection";
import { NewsDocumentsSection } from "./section/NewsDocumentsSection";
import { Editor, EditorEvents } from "@tiptap/react";
import { getHtmlStringForPreview, getHtmlStringForTipTap } from "src/utils/HTMLUtils";
import RichTextEditor from "src/components/tiptap/RichTextEditor";
import { RICH_TEXT_PARSE_OPTIONS } from "src/components/tiptap/RichTextEditor.utils";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import { NewsRealestatesSection } from "./section/NewsRealestatesSection";
import {
    getNewsRealestateRequestFields,
    getTomorrowDateInputValue,
    INewsRealestateSelection,
    isPublishfromDateInvalid
} from "./NewsEditForm.utils";

type NewsEditFormProps = {
    newsDetails?: newsitem;
    customerid?: string;
    newsid: string;
    realestateid: string;
    initialRealestateSelection: INewsRealestateSelection;
    refetchData: any;
};

interface NewsEditFormFields {
    title: string;
    lead: string;
    richTextEditorText: string;
    status: number;
    publishfrom: any;
    startnotification: boolean;
    realestates: ISelectableRealestate[];
}

const NewsEditFormBase: FunctionComponent<NewsEditFormProps> = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { uiStore, authStore, navStore, newsStore } = useContext(RootStoreContext);

    const { newsid, realestateid, initialRealestateSelection, refetchData } = props;
    const { inaccessibleRealestateIds } = initialRealestateSelection;
    const hasFeatureFlagNews = navStore.hasFeatureFlagNews;

    const initialDbFileImage =
        props.newsDetails && props.newsDetails.newsfileimage.length > 0
            ? props.newsDetails.newsfileimage[0]
            : undefined;

    const {
        selectedFiles,
        imageUrl,
        dbNewsFileImage,
        hasFileChanges,
        setHasFileChanges,
        setDbNewsFileImage,
        setImageUrl,
        getRootProps,
        getInputProps,
        onRemoveImageFile
    } = useNewsImageUpload(initialDbFileImage);

    const richTextEditorRef = useRef<Editor | null>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        uiStore.setDbNewsFiles(
            props.newsDetails && props.newsDetails.newsfiles.length > 0 ? props.newsDetails.newsfiles : []
        );
        const fileImage = props.newsDetails?.newsfileimage[0];
        setImageUrl(fileImage ? NetworkConfig.openThumbnailUrl + fileImage.fileid : "");
        setDbNewsFileImage(fileImage);
    }, [props.newsDetails, uiStore, setImageUrl, setDbNewsFileImage]);

    const [isNewNewsEntry, setIsNewNewsEntry] = useState<boolean>(!props.newsDetails);
    const [currentStatus, setCurrentStatus] = useState<FORM_STATUS>(FORM_STATUS.NONE);
    const [previewTitle, setPreviewTitle] = useState<string>(props.newsDetails?.news[0].title ?? "");
    const [previewLead, setPreviewLead] = useState<string>(props.newsDetails?.news[0].lead ?? "");
    const [richTextEditorTextState, setRichTextEditorTextState] = useState<string>(
        getHtmlStringForPreview(props.newsDetails?.news[0]?.text)
    );
    const [publishfrom, setPublishfrom] = useState<string>(props.newsDetails?.publishfrom ?? null);
    const [isNewsSavedClicked, setIsNewsSavedClicked] = useState<boolean>(false);
    // "Verwerfen" goes back to the realestates of the last save.
    const [savedRealestates, setSavedRealestates] = useState<ISelectableRealestate[]>(
        initialRealestateSelection.realestates
    );

    const [deleteNewsByIdMutation] = useMutation<DeleteNewsById, DeleteNewsByIdVariables>(DELETE_NEWS_BY_ID, {
        onCompleted: () => {
            printStatusMessage(t("screens.realestate.news_edit.delete_success"), MessageType.INFO);
        }
    });

    const [deleteNewsFileByFileIdMutation] = useMutation<DeleteNewsFileByFileId, DeleteNewsFileByFileIdVariables>(
        DELETE_NEWSFILE_BY_FILEID,
        {
            onCompleted: () => {
                printStatusMessage(t("screens.realestate.news_edit.delete_newsfiles"), MessageType.INFO);
                refetchData();
            }
        }
    );

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: { type: msgType, autoClose: msgType === MessageType.ERROR ? false : 3000 }
            });
        },
        [uiStore]
    );

    const { register, handleSubmit, formState, reset, watch, setValue, getValues } = useForm<NewsEditFormFields>({
        defaultValues: {
            title: props.newsDetails?.news[0].title ?? "",
            lead: props.newsDetails?.news[0].lead ?? "",
            richTextEditorText: getHtmlStringForPreview(props.newsDetails?.news[0].text),
            status: props.newsDetails?.state ?? 10,
            publishfrom: props.newsDetails?.publishfrom ?? null,
            startnotification: props.newsDetails?.startnotification,
            realestates: initialRealestateSelection.realestates
        }
    });
    const { dirtyFields, isDirty } = formState;

    const saveForm = useCallback(() => submitButtonRef?.current?.click(), []);

    const { saveFormModal, closeSaveFormModalAndGoBack } = useSaveUnsavedChangesInForm({
        saveFormFunction: saveForm,
        currentFormStatus: currentStatus,
        isFormDirty: isDirty
    });

    useSaveFormByShortcut({ saveFormFunction: saveForm, isFormDirty: isDirty });

    const watchTitle = watch("title");
    const watchLead = watch("lead");
    const watchRichTextEditorText = watch("richTextEditorText");
    const watchPublishfrom = watch("publishfrom");
    const watchRealestates = watch("realestates");
    const minimumPublishfromDate = getTomorrowDateInputValue();
    const isPublishfromInvalid = isPublishfromDateInvalid(watchPublishfrom);
    const hasNoRealestates = hasFeatureFlagNews && watchRealestates.length === 0;

    useEffect(() => {
        setPreviewTitle(watchTitle);
    }, [watchTitle]);
    useEffect(() => {
        setPreviewLead(watchLead);
    }, [watchLead]);
    useEffect(() => {
        setRichTextEditorTextState(getHtmlStringForPreview(watchRichTextEditorText));
    }, [watchRichTextEditorText]);

    const handleSaveBeforeUpload = useCallback(async (): Promise<boolean> => {
        if (!isNewNewsEntry) return true;

        const formData = getValues();
        if (isPublishfromDateInvalid(formData.publishfrom)) return false;

        const state = formData.status ? Number(formData.status) : 10;

        const requestBody = {
            id: newsid,
            ...getNewsRealestateRequestFields(
                hasFeatureFlagNews,
                realestateid,
                formData.realestates,
                inaccessibleRealestateIds
            ),
            state,
            language: getLang(),
            title: formData.title,
            lead: formData.lead,
            text: richTextEditorTextState,
            filechanged: false,
            file: undefined,
            filename: undefined,
            role: navStore.isCondominium ? 32 : 33,
            publishfrom: formData.publishfrom === "" ? null : formData.publishfrom,
            startnotification: formData.startnotification
        };

        const success = await newsStore.saveNews(requestBody, getRoleKey(authStore.user?.role));
        if (success) {
            setIsNewNewsEntry(false);
            setSavedRealestates(formData.realestates);
            uiStore.setHasNewNewsData(true);
            reset({ ...formData, status: state }, { keepDirty: false, keepDefaultValues: false, keepValues: false });
            refetchData();
        }
        return success;
    }, [
        isNewNewsEntry,
        getValues,
        newsid,
        hasFeatureFlagNews,
        realestateid,
        inaccessibleRealestateIds,
        richTextEditorTextState,
        navStore.isCondominium,
        newsStore,
        authStore.user?.role,
        uiStore,
        reset,
        refetchData
    ]);

    const onDeleteFile = useCallback(
        (fileId: any) => async () => {
            if (fileId) {
                await deleteNewsFileByFileIdMutation({ variables: { fileid: fileId } });
                if (props.newsDetails && props.newsDetails.newsfiles.length > 0) {
                    uiStore.setDbNewsFiles(props.newsDetails.newsfiles.filter((f) => f.fileid !== fileId));
                }
            }
        },
        [deleteNewsFileByFileIdMutation, props.newsDetails, uiStore]
    );

    const onResetForm = useCallback(() => {
        if (props.newsDetails && props.newsDetails.news.length > 0) {
            const newsTextPreview = getHtmlStringForPreview(props.newsDetails?.news[0]?.text);
            const newsTitle = props.newsDetails?.news[0]?.title ?? "";
            const newsLead = props.newsDetails?.news[0]?.lead ?? "";
            const newsState = props.newsDetails?.state ?? 10;
            reset({
                title: newsTitle,
                lead: newsLead,
                richTextEditorText: newsTextPreview,
                status: newsState,
                realestates: savedRealestates
            });

            setPreviewTitle(newsTitle);
            setPreviewLead(newsLead);
            setRichTextEditorTextState(newsTextPreview);
            richTextEditorRef.current
                ?.chain()
                ?.setContent(getHtmlStringForTipTap(newsTextPreview), { parseOptions: RICH_TEXT_PARSE_OPTIONS })
                .run();
        } else {
            reset({ title: "", lead: "", richTextEditorText: "", status: 10, realestates: savedRealestates });
            setPreviewTitle("");
            setPreviewLead("");
            setRichTextEditorTextState("");
            richTextEditorRef.current?.chain()?.setContent("").run();
        }

        const hasDbFileImage = !!props.newsDetails?.newsfileimage.length;
        if (hasDbFileImage && !dbNewsFileImage) {
            const img = props.newsDetails?.newsfileimage[0];
            setDbNewsFileImage(img);
            setImageUrl(img ? NetworkConfig.openThumbnailUrl + img.fileid : "");
        }

        if (selectedFiles.length > 0) {
            onRemoveImageFile();
        }

        setHasFileChanges(false);
    }, [
        reset,
        props.newsDetails,
        savedRealestates,
        onRemoveImageFile,
        selectedFiles.length,
        dbNewsFileImage,
        setDbNewsFileImage,
        setImageUrl,
        setHasFileChanges
    ]);

    const onDeleteNews = useCallback(async () => {
        if (isNewNewsEntry) {
            history.replace(selectRoute(Route.dashboard, authStore.user?.role, { realestateid }));
        } else {
            await deleteNewsByIdMutation({ variables: { newsid } });
            uiStore.setHasNewNewsData(true);
            history.replace(selectRoute(Route.dashboard, authStore.user?.role, { realestateid }));
        }
    }, [authStore.user?.role, history, realestateid, deleteNewsByIdMutation, isNewNewsEntry, newsid, uiStore]);

    const handleFormSubmit = useCallback(
        async (formData: NewsEditFormFields) => {
            setCurrentStatus(FORM_STATUS.SAVING_FORM);

            if (isPublishfromDateInvalid(formData.publishfrom)) {
                setCurrentStatus(FORM_STATUS.NONE);
                return;
            }

            if (formData.publishfrom && formData.publishfrom.length > 0 && formData.status === NewsState.Editing) {
                setIsNewsSavedClicked(true);
                setPublishfrom(formData.publishfrom);
                uiStore.setIsNewsPublishModalDisplayed(true);
            }

            if (!authStore.user || !authStore.token) return;

            let imageBase64String: string | undefined = undefined;
            let fileName: string | undefined = undefined;

            if (selectedFiles.length > 0) {
                fileName = selectedFiles[0].name;
                const file = selectedFiles[0];
                const fileUrl = await fileToBase64(file);
                const resizedImageDataURL = file.type !== "image/gif" ? await resizeImageFile(file) : undefined;
                imageBase64String = resizedImageDataURL ? resizedImageDataURL.split(",")[1] : fileUrl.split(",")[1];
            }

            const state = formData.status
                ? Number(formData.status)
                : (props.newsDetails?.state ?? Number(NewsState.Published));

            const requestBody = {
                id: newsid,
                ...getNewsRealestateRequestFields(
                    hasFeatureFlagNews,
                    realestateid,
                    formData.realestates,
                    inaccessibleRealestateIds
                ),
                state,
                language: getLang(),
                title: formData.title,
                lead: formData.lead,
                text: richTextEditorTextState,
                filechanged: hasFileChanges,
                file: imageBase64String ?? undefined,
                filename: imageBase64String ? fileName : undefined,
                role: navStore.isCondominium ? 32 : 33,
                publishfrom: formData.publishfrom === "" ? null : formData.publishfrom,
                startnotification: formData.startnotification
            };

            const success = await newsStore.saveNews(requestBody, getRoleKey(authStore.user?.role));

            if (success) {
                printStatusMessage(t("screens.realestate.news_edit.save_success"), MessageType.INFO);
                setSavedRealestates(formData.realestates);
                refetchData();

                if (isNewNewsEntry) {
                    uiStore.setHasNewNewsData(true);
                    setIsNewNewsEntry(false);
                }

                reset(
                    { ...formData, status: state },
                    { keepDirty: false, keepDefaultValues: false, keepValues: false }
                );
                closeSaveFormModalAndGoBack();

                if (Number(formData.status) === NewsState.Published) {
                    history.replace(
                        selectRoute(Route.realestateNewsPreview, authStore.user?.role, { realestateid, newsid })
                    );
                }
            } else {
                printStatusMessage(
                    t("screens.realestate.news_edit.save_error", { errorMessage: "" }),
                    MessageType.ERROR
                );
            }

            setCurrentStatus(FORM_STATUS.NONE);
            setHasFileChanges(false);
        },
        [
            reset,
            printStatusMessage,
            authStore.token,
            authStore.user,
            richTextEditorTextState,
            newsid,
            hasFeatureFlagNews,
            realestateid,
            inaccessibleRealestateIds,
            refetchData,
            isNewNewsEntry,
            uiStore,
            t,
            selectedFiles,
            hasFileChanges,
            history,
            props.newsDetails?.state,
            closeSaveFormModalAndGoBack,
            navStore.isCondominium,
            newsStore,
            setHasFileChanges
        ]
    );

    const onChangeState = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newsState = e.target.value;
            setValue("status", Number(newsState), { shouldDirty: true });
            if (newsState === NewsState.Published.toString() && publishfrom && publishfrom.length > 0) {
                setIsNewsSavedClicked(false);
                uiStore.setIsNewsPublishModalDisplayed(true);
            }
        },
        [publishfrom, uiStore, setValue]
    );

    const onChangeNewsTextRichTextEditorField = useCallback(
        (update: EditorEvents["update"]) => {
            const newsText = getHtmlStringForPreview(props.newsDetails?.news[0]?.text);
            if (update.editor.getText().length === 0) {
                setRichTextEditorTextState("");
                setValue("richTextEditorText", "", {
                    shouldDirty: newsText !== ""
                });
            } else {
                const editorContent = getHtmlStringForPreview(update.editor.getHTML());
                setRichTextEditorTextState(editorContent);
                setValue("richTextEditorText", editorContent, {
                    shouldDirty: editorContent !== newsText
                });
            }
        },
        [props.newsDetails?.news, setValue]
    );

    const onChangeRealestates = useCallback(
        (realestates: ISelectableRealestate[]) => {
            setValue("realestates", realestates, { shouldDirty: true });
        },
        [setValue]
    );

    const onRichTextEditorCreated = useCallback((editor: EditorEvents["create"]) => {
        richTextEditorRef.current = editor.editor;
    }, []);

    const onChangePublishfrom = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setValue("startnotification", value.length > 0);
            setValue("publishfrom", value, { shouldDirty: true });
            setPublishfrom(value);
        },
        [setValue]
    );

    const closePublishModal = useCallback(() => {
        setValue("status", NewsState.Editing, { shouldDirty: true });
        uiStore.setIsNewsPublishModalDisplayed(false);
    }, [uiStore, setValue]);

    const confirmPublishNow = useCallback(() => {
        setValue("publishfrom", "", { shouldDirty: true });
        uiStore.setIsNewsPublishModalDisplayed(false);
    }, [setValue, uiStore]);

    const onClickOpenDocumentModal = useCallback(async () => {
        const canProceed = await handleSaveBeforeUpload();
        if (canProceed) {
            uiStore.setIsAddNewsDocumentClicked(true);
        }
    }, [handleSaveBeforeUpload, uiStore]);

    const isBusy = currentStatus === FORM_STATUS.SAVING_FORM;

    return (
        <React.Fragment>
            <UI.Container className="pt-4" fluid>
                {saveFormModal}
                <UI.Row>
                    <UI.Col sm="12" lg="6" className={styles.EditFormCol}>
                        <UI.Container className={styles.EditFormContainer}>
                            <UI.Form>
                                <UI.Row>
                                    <UI.Col>
                                        <UI.Input
                                            label={t("screens.realestate.news_edit.title").toString()}
                                            {...register("title")}
                                            type="text"
                                            autoComplete="off"
                                            className={dirtyFields.title ? styles.changed : ""}
                                        />
                                    </UI.Col>
                                    <UI.Col>
                                        <UI.Input
                                            label={t("screens.realestate.news_edit.status").toString()}
                                            {...register("status")}
                                            as="select"
                                            className={dirtyFields.status ? styles.changed : ""}
                                            disabled={!isNewNewsEntry && props.newsDetails?.state !== NewsState.Editing}
                                            onChange={onChangeState}
                                        >
                                            <option value={NewsState.Editing}>
                                                {t("screens.realestate.news_edit.editing")}
                                            </option>
                                            <option value={NewsState.Published}>
                                                {t("screens.realestate.news_edit.published")}
                                            </option>
                                        </UI.Input>
                                    </UI.Col>
                                </UI.Row>
                                {hasFeatureFlagNews && (
                                    <NewsRealestatesSection
                                        realestates={watchRealestates}
                                        isCondominium={navStore.isCondominium}
                                        onChange={onChangeRealestates}
                                    />
                                )}
                                <UI.Row>
                                    <UI.Col>
                                        <UI.Input
                                            label={t("screens.realestate.news_edit.lead").toString()}
                                            {...register("lead")}
                                            type="text"
                                            as="textarea"
                                            autoComplete="off"
                                            className={`${dirtyFields.lead ? styles.changed : ""} ${styles.LeadTextArea}`}
                                        />
                                    </UI.Col>
                                </UI.Row>
                                <NewsImageSection
                                    selectedFiles={selectedFiles}
                                    dbNewsFileImage={dbNewsFileImage}
                                    getRootProps={getRootProps}
                                    getInputProps={getInputProps}
                                    onRemoveImageFile={onRemoveImageFile}
                                />
                                <UI.Row>
                                    <UI.Col>
                                        <UI.Form.Label>{t("screens.realestate.news_edit.text")}</UI.Form.Label>
                                        <RichTextEditor
                                            className={`${dirtyFields.richTextEditorText ? styles.changed : ""} form-control input-group ${styles.RichTextEditor}`}
                                            enableContentCheck={true}
                                            content={getHtmlStringForTipTap(richTextEditorTextState)}
                                            onContentUpdate={onChangeNewsTextRichTextEditorField}
                                            onEditorCreate={onRichTextEditorCreated}
                                        />
                                    </UI.Col>
                                </UI.Row>
                                <NewsDocumentsSection
                                    newsid={newsid}
                                    newsStore={newsStore}
                                    uiStore={uiStore}
                                    onBeforeFileUpload={handleSaveBeforeUpload}
                                />
                            </UI.Form>
                        </UI.Container>
                        <div className={styles.AddNewsDocument} onClick={onClickOpenDocumentModal}>
                            <UI.Icon icon={UI.SVGIcon.Add} size={14} className={styles.IconAddNewsDocument} />
                            {t("screens.realestate.news_edit.choose_document")}
                        </div>
                        {uiStore.isAddNewsDocumentClicked ? <AddNewsDocumentsModal /> : undefined}
                        <UI.Row>
                            <UI.Col>
                                <UI.Input
                                    type="date"
                                    label={t("screens.realestate.news_edit.publishfrom").toString()}
                                    className={`${styles.PublishDate} ${isPublishfromInvalid ? styles.PublishDateInvalid : ""}`}
                                    {...register("publishfrom")}
                                    onChange={onChangePublishfrom}
                                    min={minimumPublishfromDate}
                                />
                                {isPublishfromInvalid && (
                                    <div className={styles.PublishDateError}>
                                        {t("screens.realestate.news_edit.publishfrom_future_date_required")}
                                    </div>
                                )}
                                {uiStore.isNewsPublishModalDisplayed && (
                                    <NewsPublishfromModal
                                        publishfrom={publishfrom}
                                        isNewsSaved={isNewsSavedClicked}
                                        onClose={closePublishModal}
                                        onConfirm={confirmPublishNow}
                                    />
                                )}
                            </UI.Col>
                            <UI.Col className={styles.StartNotification}>
                                <UI.Checkbox
                                    label={t("screens.realestate.news_edit.notification")}
                                    {...register("startnotification")}
                                    className={dirtyFields.startnotification ? styles.changed : ""}
                                    disabled={!publishfrom || publishfrom.length === 0}
                                />
                            </UI.Col>
                        </UI.Row>
                        <UI.Row>
                            <UI.Col className="my-3">
                                <UI.Button
                                    ref={submitButtonRef}
                                    type="submit"
                                    label={isBusy ? t("labels.is_saving") : t("labels.save")}
                                    disabled={
                                        (isEmpty(dirtyFields) && !hasFileChanges) ||
                                        isBusy ||
                                        isPublishfromInvalid ||
                                        hasNoRealestates
                                    }
                                    onClick={handleSubmit(handleFormSubmit)}
                                    className={styles.NewsFormButton}
                                />
                                <UI.Button
                                    label={t("labels.discard")}
                                    disabled={(isEmpty(dirtyFields) && !hasFileChanges) || isBusy}
                                    onClick={onResetForm}
                                    className={`${styles.NewsFormButton} ml-sm-2 mt-2 mt-sm-0`}
                                    variant="outline-primary"
                                />
                                {props.newsDetails?.state === NewsState.Editing && (
                                    <UI.ConfirmationDialog
                                        key="customerDeleteDialog"
                                        buttonText={t("labels.delete")}
                                        modalTitle={t("labels.delete")}
                                        confirmationQuestion={t("screens.realestate.news_edit.delete_confirmation")}
                                        inProgressText={t("screens.realestate.news_edit.delete_in_progress")}
                                        onConfirmation={onDeleteNews}
                                        buttonClasses={`${styles.NewsFormButton} ml-sm-2 mt-2 mt-sm-0`}
                                        isDangerousOperation
                                    />
                                )}
                            </UI.Col>
                        </UI.Row>
                    </UI.Col>
                    <UI.Col sm="12" lg="6" className={`${styles.EditPreviewCol} mt-sm-4 mt-lg-0`}>
                        <NewsPreview
                            key={imageUrl}
                            title={previewTitle}
                            lead={previewLead}
                            date={props.newsDetails?.timestamp}
                            text={richTextEditorTextState}
                            imageUrl={imageUrl}
                            className={styles.NewsPreviewStyles}
                            newsFiles={uiStore.dbNewsFiles}
                            onDeleteFile={onDeleteFile}
                        />
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </React.Fragment>
    );
};

export const NewsEditForm = Sentry.withProfiler(observer(NewsEditFormBase));
